import { useDispatch } from "react-redux";

import { setSelectedProjectIdForColumns, updateProjectColumns } from "store/features";
import {
  setSelectedShapefile,
  setShapefileFeatures
} from "store/features/project/projectSlice";
import { useForecastStore } from "stores/forecast";
import { useMapStore } from "stores/map";

import { useUser } from "hooks";

import { ProjectModel } from "api/project";

import { EntityKind } from "models/entityKind";

import { useClearProjectItems } from "components/project/views/hooks/useClearProjectItems";

import { useProjectDispatch } from "../context";
import { useProjectListQuery } from "../queries";
import { setSelectedProjectIdToSession } from "../session";
import { getProjectPermissions } from "../utils";

type ChangeProjectParam = string | ProjectModel;

export function useChangeProject() {
  const projectDispatch = useProjectDispatch();
  const dispatch = useDispatch();
  const setCheckedGlobalForecasts = useForecastStore((s) => s.setCheckedGlobalForecasts);
  const { user, isAtLeastPowerUser, isAtLeastAdmin, isReadonly } = useUser();
  const projectListQuery = useProjectListQuery({});

  const clearView = useClearProjectItems({
    isClearingFilter: false
  });

  const findProject = (projectId: string): ProjectModel | undefined => {
    return projectListQuery?.data?.find((project) => project.projectId === projectId);
  };

  const changeProject = (param: ChangeProjectParam) => {
    const selectedProject = typeof param === "string" ? findProject(param) : param;
    const selectedProjectId = typeof param === "string" ? param : param.projectId;

    dispatch(setSelectedShapefile(null));
    dispatch(setShapefileFeatures({}));
    // Clear the rig/frac Color By drill-in so it can't open against a new project.
    useMapStore.getState().setSelectedRigFracLayer(null);
    // Drop any session-only value-hiding tied to the previous project's layers.
    useMapStore.getState().clearHiddenRigFracValues();
    // Drop any remembered rig/frac colouring source tied to the previous project.
    useMapStore.getState().clearRigFracColorSource();
    // Drop the focus-driven Color By overlay so it cannot carry into the new project.
    useMapStore.getState().clearRigFracFocusColorBy();
    setSelectedProjectIdToSession(selectedProjectId);
    setCheckedGlobalForecasts([]);
    dispatch(setSelectedProjectIdForColumns(selectedProjectId));
    dispatch(updateProjectColumns(EntityKind.Well));

    const nextPermissions = getProjectPermissions(
      selectedProject,
      user,
      isAtLeastPowerUser,
      isAtLeastAdmin,
      isReadonly
    );

    clearView.update(selectedProjectId);

    projectDispatch({
      payload: {
        permissions: nextPermissions,
        selectedProjectId: selectedProjectId,
        selectedViewId: undefined
      }
    });
  };

  return { update: changeProject };
}
